<script setup>
definePage({
  meta: {
    layout: 'default',
    action: 'read',
    subject: 'Role',
  },
})

const { useAbility } = await import('@casl/vue')
const ability = useAbility()
const { useApi } = await import('@/utils/api')
const router = useRouter()

// ── State ─────────────────────────────────────────────────────────────────────
const roles = ref([])
const loading = ref(false)

// Create dialog
const createDialog = ref(false)
const creating = ref(false)
const createErrors = ref({})
const createForm = ref({ name: '' })

// ── Fetch ─────────────────────────────────────────────────────────────────────
const fetchRoles = async () => {
  loading.value = true
  try {
    const response = await useApi('/api/v1/roles').get()
    const data = response?.data ?? response
    roles.value = Array.isArray(data) ? data : (data?.data ?? [])
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

onMounted(fetchRoles)

// ── Create ────────────────────────────────────────────────────────────────────
const openCreate = () => {
  createForm.value = { name: '' }
  createErrors.value = {}
  createDialog.value = true
}

const doCreate = async () => {
  creating.value = true
  createErrors.value = {}
  try {
    const response = await useApi('/api/v1/roles').post({
      body: { name: createForm.value.name, permissions: [] },
    })
    const saved = response?.data ?? response
    createDialog.value = false
    // Langsung ke halaman edit role baru untuk set permissions
    router.push({ name: 'apps-roles-id-edit', params: { id: saved.id } })
  } catch (e) {
    if (e?.response?.status === 422) {
      createErrors.value = e?.data?.errors ?? {}
    } else {
      console.error(e)
    }
  } finally {
    creating.value = false
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const permCount = (role) => role.permissions?.length ?? 0
</script>

<template>
  <div>
    <!-- Header -->
    <VRow class="mb-4">
      <VCol cols="12">
        <div class="d-flex align-center justify-space-between flex-wrap gap-3">
          <div>
            <h4 class="text-h4 font-weight-bold">Manajemen Role</h4>
            <p class="text-body-2 text-medium-emphasis mb-0">
              Atur hak akses per role untuk setiap karyawan
            </p>
          </div>
          <VBtn
            v-if="ability.can('create', 'Role')"
            prepend-icon="ri-shield-keyhole-line"
            @click="openCreate"
          >
            Tambah Role
          </VBtn>
        </div>
      </VCol>
    </VRow>

    <!-- Loading -->
    <VRow v-if="loading">
      <VCol v-for="i in 3" :key="i" cols="12" sm="6" md="4">
        <VCard>
          <VCardText>
            <VSkeletonLoader type="heading, text, text, button" />
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Empty state -->
    <VCard v-else-if="roles.length === 0">
      <VCardText class="text-center py-12">
        <VIcon icon="ri-shield-line" size="64" color="secondary" class="mb-4" />
        <p class="text-h6 text-medium-emphasis">Belum ada role</p>
        <p class="text-body-2 text-medium-emphasis mb-4">
          Buat role pertama untuk mengatur hak akses karyawan
        </p>
        <VBtn
          v-if="ability.can('create', 'Role')"
          prepend-icon="ri-add-line"
          @click="openCreate"
        >
          Buat Role
        </VBtn>
      </VCardText>
    </VCard>

    <!-- Role cards -->
    <VRow v-else>
      <VCol
        v-for="role in roles"
        :key="role.id"
        cols="12"
        sm="6"
        md="4"
      >
        <VCard height="100%">
          <VCardText class="pa-5">
            <!-- Role name + badge -->
            <div class="d-flex align-center justify-space-between mb-3">
              <div class="d-flex align-center gap-2">
                <VAvatar color="primary" variant="tonal" size="40">
                  <VIcon icon="ri-shield-user-line" size="20" />
                </VAvatar>
                <div>
                  <p class="font-weight-bold mb-0 text-body-1">{{ role.name }}</p>
                  <p class="text-body-2 text-medium-emphasis mb-0">
                    {{ permCount(role) }} permission
                  </p>
                </div>
              </div>
            </div>

            <!-- Permission preview (maks 5) -->
            <div class="d-flex flex-wrap gap-1 mb-4" style="min-height:52px;">
              <VChip
                v-for="perm in (role.permissions ?? []).slice(0, 5)"
                :key="perm"
                size="x-small"
                color="secondary"
                variant="tonal"
                label
              >
                {{ perm }}
              </VChip>
              <VChip
                v-if="(role.permissions?.length ?? 0) > 5"
                size="x-small"
                color="primary"
                variant="tonal"
                label
              >
                +{{ role.permissions.length - 5 }} lainnya
              </VChip>
              <span
                v-if="!(role.permissions?.length)"
                class="text-body-2 text-medium-emphasis"
              >
                Belum ada permission
              </span>
            </div>

            <!-- Actions -->
            <VDivider class="mb-3" />
            <VBtn
              v-if="ability.can('update', 'Role')"
              variant="tonal"
              color="primary"
              size="small"
              prepend-icon="ri-settings-3-line"
              :to="{ name: 'apps-roles-id-edit', params: { id: role.id } }"
            >
              Kelola Permission
            </VBtn>
          </VCardText>
        </VCard>
      </VCol>
    </VRow>

    <!-- Create Dialog -->
    <VDialog v-model="createDialog" max-width="440" persistent>
      <VCard>
        <VCardTitle class="pa-6 pb-0">Buat Role Baru</VCardTitle>
        <VCardText class="pa-6">
          <VTextField
            v-model="createForm.name"
            label="Nama Role *"
            placeholder="cth. kasir, supervisor, gudang"
            :error-messages="createErrors.name"
            hint="Gunakan huruf kecil tanpa spasi. cth: kasir, head-barista"
            persistent-hint
            autofocus
          />
        </VCardText>
        <VCardActions class="pa-6 pt-0">
          <VSpacer />
          <VBtn variant="tonal" color="secondary" :disabled="creating" @click="createDialog = false">
            Batal
          </VBtn>
          <VBtn color="primary" :loading="creating" @click="doCreate">
            Buat & Atur Permission
          </VBtn>
        </VCardActions>
      </VCard>
    </VDialog>
  </div>
</template>
